/**
 * The admin Live Flights page (src/pages/LiveFlights.jsx): where each
 * traveler's plane is on the map and how its card reads. Flights come from
 * GET /api/admin/live-flights (server/live-flights.js).
 */
import { airportCoordinates } from '@/data/airportCoordinates';
import { formatDelay } from '@/utils/flightStatusDisplay';
import { planeOnRoute, unwrapLongitudes } from '@/utils/flightRoute';

/** [lat, lon] of an airport from the built-in list, or null. */
export function airportPoint(code) {
  const c = airportCoordinates[String(code || '').toUpperCase()];
  return c ? [c[1], c[0]] : null;
}

/**
 * The route to draw for an airborne flight: its filed route from flight
 * tracking, or, without one, a straight line between two airports we know.
 */
export function routeFor(flight) {
  const route = flight?.position?.route;
  if (Array.isArray(route) && route.length >= 2) return unwrapLongitudes(route);
  const from = airportPoint(flight?.origin);
  const to = airportPoint(flight?.destination);
  return from && to ? unwrapLongitudes([from, to]) : null;
}

/** The status shape src/utils/flightRoute.js reads, from a live-flights row. */
const statusOf = (flight) => ({ details: { actual_off: flight?.off_at || null }, estimated_in: flight?.eta || null, scheduled_in: flight?.scheduled_in || null });

/**
 * Where to draw a flight: { route, flown, ahead, point, heading }, from its
 * reported position, else where the clock puts it along the route. Null
 * without a route.
 */
export function planeFor(flight, nowMs = Date.now()) {
  const route = routeFor(flight);
  if (!route) return null;
  const plane = planeOnRoute(route, flight.position, statusOf(flight), nowMs);
  if (!plane) return null;
  return {
    route,
    flown: [...route.slice(0, plane.after + 1), plane.point],
    ahead: [plane.point, ...route.slice(plane.after + 1)],
    point: plane.point,
    heading: plane.heading,
  };
}

/** Share of the flight flown, 0 to 1, from takeoff to the expected arrival. */
export function flightProgressOf(flight, nowMs = Date.now()) {
  const off = Date.parse(flight?.off_at || '');
  const on = Date.parse(flight?.eta || flight?.scheduled_in || '');
  if (!Number.isFinite(off) || !Number.isFinite(on) || on <= off) return 0;
  return Math.min(1, Math.max(0, (nowMs - off) / (on - off)));
}

/** A card's status pill: { label, tone } in the trip page's tones. */
export function liveFlightBadge(flight, kind) {
  if (kind === 'in_air') {
    if (flight.phase === 'diverted') return { label: 'Diverted', tone: 'warn' };
    if (flight.phase === 'taxiing') return { label: 'Taxiing out', tone: 'info' };
    const late = Number(flight.arrival_delay_minutes);
    return late >= 15 ? { label: `In the air · ${formatDelay(late)} late`, tone: 'warn' } : { label: 'In the air', tone: 'info' };
  }
  if (kind === 'landed') return flight.at_gate ? { label: 'At the gate', tone: 'good' } : { label: 'Landed', tone: 'good' };
  if (flight.state === 'cancelled') return { label: 'Cancelled', tone: 'bad' };
  const delay = Number(flight.departure_delay_minutes);
  return flight.state === 'delayed' && delay > 0 ? { label: `Delayed ${formatDelay(delay)}`, tone: 'warn' } : { label: 'On time', tone: 'good' };
}

/** "Darius (GEG50U)": the founder's way of naming a trip. */
export function tripName(trip) {
  const name = String(trip?.first_name || '').trim();
  const ref = trip?.reference || null;
  if (name && ref) return `${name} (${ref})`;
  return name || ref || 'Trip';
}

/** "37,000 ft · 523 mph", or null. */
export function altitudeLine(position) {
  if (!position) return null;
  const parts = [];
  if (Number.isFinite(position.altitude_ft) && position.altitude_ft > 0) parts.push(`${Math.round(position.altitude_ft).toLocaleString('en-US')} ft`);
  if (Number.isFinite(position.groundspeed_mph) && position.groundspeed_mph > 0) parts.push(`${position.groundspeed_mph} mph`);
  return parts.length ? parts.join(' · ') : null;
}
